import { Injectable, inject } from "@angular/core";
import { NavigationCancel, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { AuthHttpConfig } from "./auth-http-config";

/** sessionStorage key holding the page the user was on when a 401/402 forced
 *  them back to the sign-in screen, or that they opened while signed out.
 *  Deliberately distinct from
 *  MS_GRAPH_RETURN_URL_KEY, which covers the Microsoft OAuth round trip. */
export const RETURN_URL_KEY = "return_url";

@Injectable({ providedIn: "root" })
export class ReturnUrlService {
  /** Remembers where to send the user back to once they sign in again.
   *  The sign-in screen itself and the bare root are not worth restoring. */
  public capture(url: string): void {
    if (
      !url ||
      url === "/" ||
      /^\/(login|logout|exit|register|registered|confirmed|reset|join|activate)\b/.test(
        url,
      )
    ) {
      return;
    }

    sessionStorage.setItem(RETURN_URL_KEY, url);
  }

  /** Returns the stored URL once — a second call gets null. */
  public consume(): string {
    const url = sessionStorage.getItem(RETURN_URL_KEY);
    this.clear();

    return url;
  }

  /** A page opened while signed out is rejected by the permission guards,
   *  which bounce it to "/" and from there to the sign-in screen — remember
   *  it so signing in returns there. Runs in an injection context, from
   *  AppComponent's constructor, so it sees the initial navigation too. */
  public captureSignedOutDeepLinks(): Subscription {
    const httpConfig = inject(AuthHttpConfig);

    return inject(Router)
      .events.pipe(
        filter((event) => event instanceof NavigationCancel),
        filter(() => !httpConfig.getToken()),
      )
      .subscribe((event) => this.capture(event.url));
  }

  public clear(): void {
    sessionStorage.removeItem(RETURN_URL_KEY);
  }
}
